import { useState } from "react";
import { toast } from "sonner";
import { Check, Trash2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { ReviewPhotoStrip } from "@/components/review/ReviewPhotoStrip";

const errorText = (err, fallback) => (typeof err?.response?.data?.detail === "string" ? err.response.data.detail : fallback);

/** Customer-submitted reviews waiting for David's OK — Approve publishes to the home page (and thanks the reviewer by email). */
export function PendingReviews({ reviews, onChanged }) {
  const [busy, setBusy] = useState("");
  const pending = reviews.filter((r) => r.status === "pending");
  if (pending.length === 0) return null;

  const act = async (id, fn, ok, fail) => {
    setBusy(id);
    try { await fn(); toast.success(ok); onChanged(); }
    catch (err) { toast.error(errorText(err, fail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-2xl border border-[#FFD600]/40 bg-[#FFD600]/5 p-5 space-y-4" data-testid="pending-reviews">
      <div className="dg-eyebrow flex items-center gap-2 text-[#FFD600]"><Clock className="w-3 h-3" /> {pending.length} review{pending.length === 1 ? "" : "s"} waiting for your OK — not on the site yet</div>
      {pending.map((r) => (
        <div key={r.id} className="dg-card rounded-xl p-4" data-testid={`pending-review-${r.id}`}>
          <div className="text-amber-400 text-sm font-mono">{"★".repeat(r.stars)}</div>
          <p className="text-slate-200 text-sm mt-2 whitespace-pre-wrap break-words">"{r.quote}"</p>
          <div className="mt-3 text-xs text-slate-500 font-mono uppercase tracking-widest">{r.name}{r.where ? ` · ${r.where}` : ""}{r.email ? ` · ${r.email}` : " · no email"} · {new Date(r.created_at).toLocaleDateString()}</div>
          <div className="mt-3"><ReviewPhotoStrip id={r.id} photos={r.photos} size="w-24 h-24" /></div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" disabled={busy === r.id} onClick={() => act(r.id, () => api.post(`/admin/reviews/${r.id}/approve`), "Published to the home page", "Could not approve")}
              className="dg-btn-primary rounded-full px-4 py-2.5 min-h-[44px] text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid={`pending-approve-${r.id}`}>
              <Check className="w-4 h-4" /> Approve & publish
            </button>
            <button type="button" disabled={busy === r.id} onClick={() => act(r.id, () => api.delete(`/admin/reviews/${r.id}`), "Review deleted", "Could not delete")}
              className="dg-btn-ghost rounded-full px-4 py-2.5 min-h-[44px] text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid={`pending-delete-${r.id}`}>
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
